import { useEffect, useMemo, useState } from "react";
import { generatePath, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Utils
import constants from "#src/config/constants";
import { pathname } from "#src/config/pathname";

// Assets
import { Download02 } from "@untitled-ui/icons-react";

// API related
import directoryService from "#src/services/endpoint/directory";
import useFetchPaginated from "#src/hooks/useFetchPaginated";
import { IDirectoryUser } from "#src/services/endpoint/directory/types";

// Components
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { ButtonV2 } from "#src/commonV2/Button";
import { createToast } from "#src/common/system/toasts";

// Children
import { UsersTable } from "#src/components/endpoint/directory/users/UsersTable";
import { UsersFilter } from "#src/components/endpoint/directory/users/UsersFilter";
import { EditUserModal } from "#src/components/endpoint/directory/users/EditUserModal";
import { SyncWithGoogleBtn } from "#src/components/endpoint/directory/users/SyncWithGoogleBtn";
import useFetchNoPaging from "#src/hooks/useFetchNoPaging";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";

export const DirectoryUsers: React.FC = () => {
  const { workspaceId } = useParams<"workspaceId">();
  const { t } = useTranslation("endpoint", { keyPrefix: "directory" });

  if (!workspaceId) {
    return null;
  }

  // --------------- INITIALIZATION ---------------

  const fetchDepartmentParams = useMemo<
    Parameters<typeof directoryService.listAllDepartmentsWithPath>
  >(() => [workspaceId], [workspaceId]);

  const { list: departmentList, isLoading: isLoadingDepartment } =
    useFetchNoPaging(
      directoryService.listAllDepartmentsWithPath,
      fetchDepartmentParams
    );

  const departmentsOptions = useMemo(() => {
    if (departmentList === null) return [];
    const res: IFilterItemV2[] = departmentList.map((item) => ({
      key: item.id,
      value: item.id,
      getLabel: () => item.path,
    }));
    return res;
  }, [departmentList]);

  const { value: department, renderProps: departmentFilterRenderProps } =
    useFilter({
      label: t("table.columns.department"),
      selectionList: departmentsOptions,
      options: {
        searchParamKey: "department",
        sideEffectOnChange: () => {
          setSelectedPage(1);
        },
      },
      isLoading: isLoadingDepartment,
    });

  // --------------- DATA ---------------

  const [userList, setUserList] = useState<IDirectoryUser[]>([]);

  // Pagination
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(constants.DEFAULT_PAGE_SIZE);

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");

  // Selection
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Modals
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);

  // Export
  const [isExporting, setIsExporting] = useState(false);

  // --------------- COMPUTED ---------------

  const fetchParams = useMemo<Parameters<typeof directoryService.listUsers>>(
    () => [
      workspaceId,
      {
        q: searchKeyword,
        page: selectedPage,
        size: pageSize,
        sort: sortOrder ?? undefined,
        department_id: department,
      },
    ],
    [workspaceId, searchKeyword, selectedPage, pageSize, sortOrder, department]
  );

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return userList.find((user) => user.id === selectedUserId) || null;
  }, [userList, selectedUserId]);

  // --------------- HOOKS ---------------

  const {
    list: _userList,
    count: userCount,
    isLoading,
    updateData: fetchUserList,
  } = useFetchPaginated(directoryService.listUsers, fetchParams);

  // --------------- METHODS ---------------

  const handleExportUsers = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await directoryService.exportUsers(workspaceId, t);
    } catch (error) {
      console.error("Failed to export users:", error);
      createToast({
        type: "error",
        message: t("export.notification.error"),
      });
    } finally {
      setIsExporting(false);
    }
  };

  // ---------------EFFECTS ---------------

  useEffect(() => {
    setUserList(_userList || []);
  }, [_userList]);

  // --------------- RENDER ---------------

  return (
    <div className="pt-5 pb-6 flex flex-col gap-5 min-h-screen">
      {/* Header (Breadcrumb + title) */}
      <div className="px-8 flex flex-col gap-4">
        <BreadcrumbV2
          levels={[
            {
              name: t("breadcrumb.levels.endpoint"),
              path: generatePath(pathname.ENDPOINT_OVERVIEW, { workspaceId }),
            },
          ]}
          currentPage={{
            name: t("breadcrumb.current.directory"),
            tab: t("breadcrumb.current.users"),
          }}
        />

        <div className="flex items-center justify-between flex-wrap gap-4">
          <h1 className="text-display-xs font-semibold text-gray-v2-900">
            {t("breadcrumb.current.users")}
          </h1>

          <div className="flex items-center gap-2">
            <ButtonV2
              variant="secondary"
              size="md"
              color="brand"
              pending={isExporting}
              onClick={handleExportUsers}
            >
              {!isExporting && <Download02 className="h-5 w-5" />}
              <p>{t("export.btn")}</p>
            </ButtonV2>

            <SyncWithGoogleBtn
              workspaceId={workspaceId}
              onSuccess={fetchUserList}
            />
          </div>
        </div>
      </div>
      {/* Header (Breadcrumb + title) end */}

      <div className="px-8 flex flex-col gap-4 flex-1">
        {/* Filter */}
        <UsersFilter
          selectedUserIds={selectedUserIds}
          searchKeyword={searchKeyword}
          setSearchKeyword={setSearchKeyword}
          departmentFilterRenderProps={departmentFilterRenderProps}
        />
        {/* Filter end */}

        {/* Table */}
        <UsersTable
          userList={userList}
          isLoading={isLoading}
          selectedUserIds={selectedUserIds}
          setSelectedUserIds={setSelectedUserIds}
          setSelectedPage={setSelectedPage}
          setPageSize={setPageSize}
          userCount={userCount}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          selectedPage={selectedPage}
          pageSize={pageSize}
          openEditUserModal={(id: string) => {
            setSelectedUserId(id);
            setIsEditUserModalOpen(true);
          }}
        />
        {/* Table end */}
      </div>

      {/* ------- MODALS ------- */}

      <EditUserModal
        workspaceId={workspaceId}
        isOpen={isEditUserModalOpen}
        closeModal={() => {
          setIsEditUserModalOpen(false);
          setSelectedUserId(null);
        }}
        selectedUser={selectedUser}
        onSuccess={(newData) => {
          setUserList((prev) =>
            prev.map((user) => (user.id === newData.id ? newData : user))
          );
        }}
        departments={departmentList ?? []}
      />

      {/* ------- MODALS end ------- */}
    </div>
  );
};
